import * as Sentry from '@sentry/react'
import { getApiUrl, getProcurementUrl } from '@shared/utils'
import axios from 'axios'
import { SSO_STRATEGIES } from './apiRoutes'
import {
  AUTH_LOGIN,
  PASSWORD_RESET,
  PASSWORD_RESET_EMAIL,
  SSO_AUTH_LOGIN,
  SSO_AUTH_SIGNUP,
  TOKEN_LOCAL_STORAGE,
} from './data/authentication.service'
import { OpenAPI } from './types/generated-types'
import { OpenAPI as ProcurementOpenAPI } from './types/procurement'

const UNPROTECTED_ROUTES = [
  AUTH_LOGIN,
  SSO_AUTH_LOGIN,
  SSO_AUTH_SIGNUP,
  PASSWORD_RESET_EMAIL,
  PASSWORD_RESET,
  SSO_STRATEGIES,
  '/custom-reports/pdf-access',
  '/v2/invitations/validate',
  '/v2/invitations/accept',
]

// set base urls for axios and openApi (OpenApi is used for convenience controller methods via our automatic client generator from swagger)
const baseUrl = getApiUrl()
OpenAPI.BASE = baseUrl
OpenAPI.TOKEN = localStorage.getItem(TOKEN_LOCAL_STORAGE) ?? undefined

// Initialization for Procurement OpenAPI
const procurementBaseUrl = getProcurementUrl()
ProcurementOpenAPI.BASE = procurementBaseUrl
ProcurementOpenAPI.TOKEN = localStorage.getItem(TOKEN_LOCAL_STORAGE) ?? undefined

const ecoplanetAxios = axios.create({
  baseURL: baseUrl,
})

// Some endpoints (e.g. /locations/enabled-modules) are known to 401 on the very first request
// after a fresh token is set, and rely on react-query retries to recover. During a short window
// after updateAuthToken we skip the auto-logout so those retries can succeed.
let lastAuthTokenUpdateAt = 0
const POST_AUTH_UPDATE_401_GRACE_MS = 3000
const isWithinPostAuthUpdateGrace = () => Date.now() - lastAuthTokenUpdateAt < POST_AUTH_UPDATE_401_GRACE_MS

// Synthetic error used so 401/403 captures group cleanly in Sentry by status + layer.
class ApiAuthError extends Error {
  constructor(status: number, layer: string) {
    super(`API ${status} (${layer})`)
    this.name = 'ApiAuthError'
  }
}

// Suppress duplicate captures for the same status+url within a short window so a burst
// of parallel requests failing on an expired token doesn't create an event storm.
const recentAuthCaptures = new Map<string, number>()
const AUTH_CAPTURE_DEDUP_MS = 10_000

// Evict entries older than the dedup window so the map stays bounded to only
// captures within the current window (query-varying urls are high-cardinality).
const pruneAuthCaptures = () => {
  const now = Date.now()
  for (const [key, timestamp] of recentAuthCaptures.entries()) {
    if (now - timestamp >= AUTH_CAPTURE_DEDUP_MS) {
      recentAuthCaptures.delete(key)
    }
  }
}

const captureAuthError = (params: {
  status: number
  layer: 'axios' | 'openapi' | 'procurement'
  url: string
  isProtectedRoute: boolean
  withinGrace: boolean
}) => {
  const { status, layer, url, isProtectedRoute, withinGrace } = params
  const key = `${status}:${url}`
  const now = Date.now()
  const last = recentAuthCaptures.get(key)
  if (last && now - last < AUTH_CAPTURE_DEDUP_MS) return
  pruneAuthCaptures()
  recentAuthCaptures.set(key, now)

  Sentry.captureException(new ApiAuthError(status, layer), {
    level: 'warning',
    tags: {
      auth_capture: 'true',
      http_status: status,
      http_layer: layer,
      is_protected_route: isProtectedRoute,
      within_grace: withinGrace,
    },
    extra: {
      url,
      page_url: window.location.href,
    },
  })
}

export const updateAuthToken = (token: string | null) => {
  lastAuthTokenUpdateAt = Date.now()
  if (token) {
    localStorage.setItem(TOKEN_LOCAL_STORAGE, token)
    OpenAPI.TOKEN = token
    ProcurementOpenAPI.TOKEN = token
  } else {
    localStorage.removeItem(TOKEN_LOCAL_STORAGE)
    OpenAPI.TOKEN = undefined
    ProcurementOpenAPI.TOKEN = undefined
  }
}

ecoplanetAxios.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_LOCAL_STORAGE)
  const { url = '' } = config

  if (isProtectedRoute(url) && isTokenEmptyOrMissing(token)) {
    localStorage.clear()
    window.location.reload()
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

ecoplanetAxios.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status
    const url = error.config?.url ?? ''
    if (status === 401 || status === 403) {
      captureAuthError({
        status,
        layer: 'axios',
        url,
        isProtectedRoute: isProtectedRoute(url),
        withinGrace: isWithinPostAuthUpdateGrace(),
      })
    }
    if (status === 401 && !isWithinPostAuthUpdateGrace()) {
      if (isProtectedRoute(url)) {
        console.log('401', url)
        //localStorage.clear()
        //window.location.reload()
      }
    }
    return Promise.reject(error)
  }
)

OpenAPI.interceptors.response.use(response => {
  const path = response.url.replace(baseUrl, '')
  if (response.status === 401 || response.status === 403) {
    captureAuthError({
      status: response.status,
      layer: 'openapi',
      url: response.url,
      isProtectedRoute: isProtectedRoute(path),
      withinGrace: isWithinPostAuthUpdateGrace(),
    })
  }
  if (response.status === 401 && !isWithinPostAuthUpdateGrace()) {
    if (isProtectedRoute(path)) {
      localStorage.clear()
      window.location.reload()
    }
  }
  return response
})

// For Procurement OpenAPI
ProcurementOpenAPI.interceptors.request.use(request => {
  const token = localStorage.getItem(TOKEN_LOCAL_STORAGE)
  if (isTokenEmptyOrMissing(token)) {
    localStorage.clear()
    window.location.reload()
  }
  return request
})

ProcurementOpenAPI.interceptors.response.use(response => {
  const path = response.url.replace(procurementBaseUrl, '')
  if (response.status === 401 || response.status === 403) {
    captureAuthError({
      status: response.status,
      layer: 'procurement',
      url: response.url,
      isProtectedRoute: isProtectedRoute(path),
      withinGrace: isWithinPostAuthUpdateGrace(),
    })
  }
  if (response.status === 401 && !isWithinPostAuthUpdateGrace()) {
    if (isProtectedRoute(path)) {
      localStorage.clear()
      window.location.reload()
    }
  }
  return response
})

const isProtectedRoute = (route: string) => {
  const potentialBasePath = new URL(`${baseUrl}`).pathname
  const urlObj = new URL(`${baseUrl}${route}`)
  urlObj.search = ''
  urlObj.hash = ''
  let cleanRoute = urlObj.pathname
  if (potentialBasePath !== '/' && potentialBasePath.length > 1 && cleanRoute.startsWith(potentialBasePath)) {
    cleanRoute = cleanRoute.replace(potentialBasePath, '')
  }

  // Check if the cleanRoute starts with any of the UNPROTECTED_ROUTES
  return !UNPROTECTED_ROUTES.some(unprotectedRoute => cleanRoute.startsWith(unprotectedRoute))
}

export const isTokenEmptyOrMissing = (token: string | null) => {
  return !token || token === ''
}

export default ecoplanetAxios
